/**
 * Hover explanation on a near-black chip, drawn as finixt-ui's Tooltip on
 * Radix Tooltip, which also shows it on focus so the keyboard sees it.
 * Tooltips name what a control does; they never carry the only copy of
 * important information.
 *
 * The child sits in an inline span that Radix anchors to, because finixt-ui's
 * buttons forward no ref; focus and hover bubble from the child to the span.
 */
import * as RadixTooltip from '@radix-ui/react-tooltip';
import type { ReactNode } from 'react';

const DELAY_MS = 300;
const Z_INDEX = 60;

export interface TooltipProps {
  label: string;
  side?: 'top' | 'bottom' | 'left' | 'right' | undefined;
  /** One focusable element; finixt-ui's buttons work here too. */
  children: ReactNode;
}

export function Tooltip({ label, side = 'top', children }: TooltipProps) {
  return (
    <RadixTooltip.Provider delayDuration={DELAY_MS}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>
          <span style={{ display: 'inline-flex' }}>{children}</span>
        </RadixTooltip.Trigger>
        <RadixTooltip.Portal>
          <RadixTooltip.Content
            side={side}
            sideOffset={6}
            style={{
              zIndex: Z_INDEX,
              background: 'var(--surface-inverse)',
              color: 'var(--text-inverse)',
              fontSize: 'var(--text-meta)',
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              padding: '5px 7px',
              borderRadius: 'var(--radius-chip)',
              animation: 'popin var(--dur-fast) var(--ease-out)',
              pointerEvents: 'none',
            }}
          >
            {label}
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
}
